import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useDataScope } from "@/components/common/DataScope";
import { useAuth } from "@/context/AuthContext";

const TTL = 60_000;
const cache = new Map();     // month key -> { ts, data }
const inflight = new Map();  // month key -> promise

// Reasons that mean "this month simply cannot have data" -> render "—", not a gap.
const NOT_APPLICABLE = new Set(["pos_has_no_data", "future"]);

function load(key) {
    const hit = cache.get(key);
    if (hit && Date.now() - hit.ts < TTL) return Promise.resolve(hit.data);
    if (inflight.has(key)) return inflight.get(key);
    const p = api
        .get("/data-status", { params: key ? { month: key } : {} })
        .then((r) => {
            cache.set(key, { ts: Date.now(), data: r.data });
            return r.data;
        })
        .catch(() => null)
        .finally(() => inflight.delete(key));
    inflight.set(key, p);
    return p;
}

export function useDataStatus(month, enabled = true) {
    const key = month || "";
    const [data, setData] = useState(() => cache.get(key)?.data ?? null);
    useEffect(() => {
        if (!enabled) return undefined;
        let alive = true;
        load(key).then((d) => {
            if (alive && d) setData(d);
        });
        return () => {
            alive = false;
        };
    }, [key, enabled]);
    return enabled ? data : null;
}

/** Everything an empty card needs to be honest: is it "not applicable" (—),
 *  what is the one-line reason, and may THIS user open the screen that fixes it. */
export function useDataGap(dataset, monthOverride) {
    const scope = useDataScope();
    const { isSuperAdmin, isSystemAdmin, hasCap } = useAuth();
    const month = monthOverride || scope.month || "";
    const status = useDataStatus(month, !!dataset);
    const ds = dataset ? status?.datasets?.[dataset] : null;
    const reason = ds?.reason || null;
    const cap = reason?.fix_cap;

    const canFix =
        !cap ? true : cap === "super" ? isSuperAdmin : cap === "infrastructure" ? isSystemAdmin : hasCap(cap);

    return {
        known: !!reason,
        code: reason?.code || null,
        na: !!reason && NOT_APPLICABLE.has(reason.code),
        reason,
        fix: reason?.fix && canFix ? reason.fix : null,
        month: status?.month || month || null,
        backfillRunning: !!status?.backfill_running,
        backfillDate: status?.backfill_current_date || null,
    };
}
